import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BlogPostShell, POSTS } from "@/pages/Blog";

const POST = POSTS.find((p) => p.slug === "organising-a-camera-club-competition");
const OG = "https://cameraclubscore.com/og/blog-generic.png";
const URL = "https://cameraclubscore.com/blog/organising-a-camera-club-competition";

export default function PostOrganising() {
  return (
    <>
      <Helmet>
        <title>{`${POST.title} — Camera Club Score`}</title>
        <meta name="description" content={POST.excerpt} />
        <link rel="canonical" href={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={URL} />
        <meta property="og:title" content={POST.title} />
        <meta property="og:description" content={POST.excerpt} />
        <meta property="og:image" content={OG} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG} />
      </Helmet>
      <BlogPostShell post={POST}>
        <p className="lead">
          Every camera club runs competitions differently. But most of the
          things that go wrong on judging night — mislaid prints, wrong
          scores, unhappy members — come from the same handful of decisions
          made (or not made) months earlier. This is the complete guide,
          the one we wish we'd had when we first joined a committee.
        </p>

        <h2>How many competitions should you hold per season?</h2>
        <p>
          Most clubs run somewhere between six and twelve competitions per
          season, alternating <strong>Print</strong> and <strong>PDI</strong>{" "}
          (Projected Digital Image) rounds. Six lets everyone breathe;
          twelve keeps engagement high but tests the committee. Eight is
          the sweet spot for a typical UK club with 40–80 members.
        </p>

        <h2>What competition categories should you have?</h2>
        <p>
          Categories help members with different skill levels and interests
          find their footing. The three most common patterns:
        </p>
        <ul>
          <li><strong>Open + themed</strong> — one open round per programme, one themed. Themes rotate: "Nature," "Street," "Monochrome," "Movement."</li>
          <li><strong>Beginner / Advanced streams</strong> — two parallel leagues so newcomers aren't measured against 20-year veterans.</li>
          <li><strong>Print / PDI split</strong> — separate season leagues so print-lovers aren't penalised by digital-heavy months.</li>
        </ul>

        <h2>What are the best competition rules for a camera club?</h2>
        <p>
          Simple, written down, and applied consistently. Every rulebook we've
          seen collapses under its own weight after three years. Ours would
          fit on a postcard:
        </p>
        <ul>
          <li>Members enter with a photographer code (short unique ID).</li>
          <li>Each entry has one title, one score, one placement (optional).</li>
          <li>Scores out of 10 or 20, judge's choice.</li>
          <li>Practice rounds don't count toward the season league.</li>
          <li>Anything else is up to the judge on the night.</li>
        </ul>

        <h2>Should print and digital competitions be judged differently?</h2>
        <p>
          Same scoring scale, same criteria, but present them differently.
          Prints need to be viewed under consistent lighting (put them on
          the stand, don't hold them up). PDIs need a properly-calibrated
          projector and a darkened room. That's the entire difference.
        </p>

        <h2>How do we keep competition judging fair?</h2>
        <p>
          Three habits, together, do 90% of the work:
        </p>
        <ol>
          <li>
            <strong>Anonymise the running order.</strong> Judges shouldn't
            see photographer names during scoring, only codes and titles.
          </li>
          <li>
            <strong>Rotate judges.</strong> Same judge every month means
            same aesthetic taste winning every month. Aim for three or four
            different visiting judges per season.
          </li>
          <li>
            <strong>Publish everything.</strong> If members can see every
            score for every image, complaints of unfairness evaporate.
            Camera Club Score's public portal was designed for this.
          </li>
        </ol>

        <h2>How do we organise themed competitions?</h2>
        <p>
          Announce themes at the start of the season, not week-by-week.
          Give members eight to twelve weeks lead time — that's the
          difference between "I'll shoot something for this" and "I'll
          scroll through my back-catalogue." Themes drive better
          photography.
        </p>

        <h2>How does software help?</h2>
        <p>
          A tool like <Link to="/">Camera Club Score</Link> handles the
          plumbing so the committee focuses on the members: photographers,
          entries, live scoring, league tables, certificates. From £60/year
          with a 30-day free trial.
        </p>
      </BlogPostShell>
    </>
  );
}
